/** Élément regroupable par catégorie d'UL (champs lecture API BL-0309). */
export interface U2104CategoryGroupable {
  id?: string;
  logicalUnitCode?: string;
  logicalUnitCategoryId?: string | null;
  logicalUnitCategoryCode?: string | null;
  logicalUnitCategoryLabelFr?: string | null;
  logicalUnitCategoryLabelEn?: string | null;
  logicalUnitCategorySortOrder?: number | null;
}

export interface U2104LogicalUnitCategoryGroup<T> {
  /** Id catégorie ou `__none__` sans catégorie. */
  key: string;
  sortOrder: number;
  label: string;
  items: T[];
}

const NONE_KEY = '__none__';
const NONE_SORT_ORDER = Number.MAX_SAFE_INTEGER;

function resolveCategoryLabel(
  item: U2104CategoryGroupable,
  locale: string,
  noCategoryLabel: string,
): string {
  if (!item.logicalUnitCategoryId) {
    return noCategoryLabel;
  }
  const isEn = locale.startsWith('en');
  const label = isEn
    ? item.logicalUnitCategoryLabelEn || item.logicalUnitCategoryLabelFr
    : item.logicalUnitCategoryLabelFr || item.logicalUnitCategoryLabelEn;
  return label || item.logicalUnitCategoryCode || noCategoryLabel;
}

function compareCode(a?: string, b?: string): number {
  return (a ?? '').localeCompare(b ?? '', undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Regroupe des UL plates par catégorie (présentation pure).
 * - Groupes triés par sortOrder (nulls last), puis label
 * - Items triés par logicalUnitCode dans chaque groupe
 * - Sans catégorie → groupe terminal « Sans catégorie »
 */
export function u2104GroupLogicalUnitsByCategory<T extends U2104CategoryGroupable>(
  items: readonly T[],
  options: { noCategoryLabel: string; locale?: string },
): U2104LogicalUnitCategoryGroup<T>[] {
  const locale = options.locale ?? 'fr';
  const map = new Map<string, U2104LogicalUnitCategoryGroup<T>>();

  for (const item of items) {
    const key = item.logicalUnitCategoryId ?? NONE_KEY;
    const sortOrder =
      item.logicalUnitCategoryId == null
        ? NONE_SORT_ORDER
        : item.logicalUnitCategorySortOrder == null
          ? NONE_SORT_ORDER - 1
          : Number(item.logicalUnitCategorySortOrder);
    const label = resolveCategoryLabel(item, locale, options.noCategoryLabel);

    let group = map.get(key);
    if (!group) {
      group = { key, sortOrder, label, items: [] };
      map.set(key, group);
    }
    group.items.push(item);
  }

  const groups = Array.from(map.values());
  for (const group of groups) {
    group.items.sort((a, b) => compareCode(a.logicalUnitCode, b.logicalUnitCode));
  }

  groups.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });

  return groups;
}

/** Aplatit les groupes en liste plate (ordre catégories + code UL). */
export function u2104FlattenLogicalUnitCategoryGroups<T>(
  groups: readonly U2104LogicalUnitCategoryGroup<T>[],
): T[] {
  return groups.flatMap((g) => g.items);
}
